/**
 * Bridge DealEasy volume-discount cart nudges into the CRO Agent cart drawer.
 *
 * Two blockers without this bridge:
 * 1) CRO mounts line items in #cro-cart-shadow-host (open Shadow DOM). DealEasy
 *    uses document.querySelector(All), which cannot see those nodes.
 * 2) lb-vd-nudge is a light-DOM Stencil component whose CSS is injected into
 *    document.head — those rules do not apply inside CRO's shadow tree.
 *
 * Handler lives at window.lbDealeasy.vdWidgetHandler (not window.vdWidgetHandler).
 *
 * DealEasy VD Nudge widget → Cart drawer selectors (relative to the shadow root;
 * this bridge pierces #cro-cart-shadow-host so plain selectors work):
 *   Line item:          .cro-line-item
 *   Insert target:      .cro-line-item__content
 *   Insert position:    beforeend
 *
 * Do not use #cro-cart-shadow-host::part(...) — querySelector cannot match ::part().
 *
 * Reliability notes:
 * - CRO often fetches with a Request object (or a captured window.fetch). DealEasy
 *   only treats string URLs as cart actions, so it never computes promotions.
 *   This bridge normalizes cart fetch/XHR URLs and dispatches
 *   lb-dealeasy-network-request so DealEasy's pipeline actually runs.
 * - CRO rebuilds line items several times after ATC. Waiting for a quiet period
 *   makes the first nudge late; injecting then letting CRO destroy the node
 *   causes appear/disappear/reappear. We park and reparent the same custom
 *   element instead of recreating it.
 */
(function () {
  "use strict";

  if (window.__dealeasyCroCartBridge) return;
  window.__dealeasyCroCartBridge = true;

  var HOST_SELECTOR = "#cro-cart-shadow-host";
  var LINE_SELECTOR = ".cro-line-item";
  var TARGET_SELECTOR = ".cro-line-item__content";
  var POSITION = "beforeend";
  var PARK_ID = "dealeasy-vd-nudge-park";
  var STYLE_ID = "dealeasy-vd-nudge-bridge-styles";
  var CART_ACTION_PATHS = [
    "/cart/add",
    "/cart/update",
    "/cart/change",
    "/cart/clear",
  ];
  var NOTIFY_MS = 80;

  // Copied from DealEasy lb-vd-nudge.entry.js (component has no shadow encapsulation).
  // Keep existing mounts visible — never auto-hide while settling/re-checking.
  var NUDGE_CSS =
    "lb-vd-nudge{--lb-vd-nudge-width:100%;--lb-vd-nudge-margin:0px;--lb-vd-nudge-padding:0px;" +
    "--lb-vd-nudge-discount-text-size:14px;--lb-vd-nudge-discount-text-weight:normal;" +
    "--lb-vd-nudge-discount-text-color:#222222;--lb-vd-nudge-highlight-text-size:14px;" +
    "--lb-vd-nudge-highlight-text-weight:normal;--lb-vd-nudge-highlight-text-color:#222222;" +
    "--lb-vd-nudge-atc-btn-text-size:12px;--lb-vd-nudge-atc-btn-text-weight:normal;" +
    "--lb-vd-nudge-atc-btn-text-color:#ffffff;--lb-vd-nudge-atc-btn-bg-color:#222222;" +
    "--lb-vd-nudge-atc-btn-border-radius:0px;--lb-vd-nudge-atc-btn-border:none}" +
    "lb-vd-nudge{visibility:visible!important;width:var(--lb-vd-nudge-width);" +
    "margin:var(--lb-vd-nudge-margin)!important; margin-top:8px!important; padding:var(--lb-vd-nudge-padding)!important;" +
    "box-sizing:border-box;font-family:inherit;display:flex;justify-content:start;" +
    "flex-wrap:wrap;flex-direction:row;align-items:center;gap:6px}" +
    ".lb-vd-nudge-discount-text{font-size:var(--lb-vd-nudge-discount-text-size);" +
    "font-weight:var(--lb-vd-nudge-discount-text-weight);color:var(--lb-vd-nudge-discount-text-color);" +
    "overflow:hidden;word-wrap:normal;text-overflow:ellipsis}" +
    ".lb-vd-nudge-discount-text span{font-size:var(--lb-vd-nudge-highlight-text-size);" +
    "font-weight:var(--lb-vd-nudge-highlight-text-weight);color:var(--lb-vd-nudge-highlight-text-color)}" +
    ".lb-vd-nudge-atc-btn{min-height:calc(var(--lb-vd-nudge-atc-btn-text-size) + 0.75rem);" +
    "width:fit-content;height:auto;display:inline-block;font-family:inherit;cursor:pointer;" +
    "padding:6px 10px;font-size:var(--lb-vd-nudge-atc-btn-text-size);" +
    "font-weight:var(--lb-vd-nudge-atc-btn-text-weight);color:var(--lb-vd-nudge-atc-btn-text-color);" +
    "background-color:var(--lb-vd-nudge-atc-btn-bg-color);" +
    "border-radius:var(--lb-vd-nudge-atc-btn-border-radius);border:var(--lb-vd-nudge-atc-btn-border);" +
    "position:relative;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}" +
    ".lb-vd-nudge-atc-btn span{font-size:var(--lb-vd-nudge-highlight-text-size);" +
    "font-weight:var(--lb-vd-nudge-highlight-text-weight);color:var(--lb-vd-nudge-highlight-text-color)}" +
    ".lb-vd-nudge-atc-btn:disabled{opacity:0.6;cursor:default}" +
    ".lb-vd-nudge-loading-spinner{width:var(--lb-vd-nudge-atc-btn-text-size);" +
    "height:var(--lb-vd-nudge-atc-btn-text-size);vertical-align:middle;position:absolute;" +
    "left:50%;top:50%;transform:translate(-50%,-50%)}" +
    ".lb-vd-nudge-loading-spinner path{fill:var(--lb-vd-nudge-atc-btn-text-color)}" +
    // Must live in the shadow stylesheet — document <style> cannot pierce #cro-cart-shadow-host.
    "lb-vd-nudge{padding-top:8px!important}" +
    // Avoid a flash of an empty custom element before Stencil paints.
    "lb-vd-nudge:not(:has(.lb-vd-nudge-discount-text)){visibility:hidden!important;height:0;overflow:hidden;" +
    "padding:0!important;margin:0!important}" +
    "#" +
    PARK_ID +
    "{position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden;visibility:hidden;pointer-events:none}" +
    ".cro-line-item__footer,[part=\"line-item-footer\"]{margin-bottom:8px!important}";

  var shadowObserved = false;
  var patchedHandler = false;
  var bridgeInjecting = false;
  var pendingRestoreAfterInject = false;
  var lastInjectFingerprint = "";
  var lastDomSignature = "";
  var nudgeListenersRegistered = false;
  var pendingPosition = null;
  var pendingPromotions = null;
  var notifyTimer = null;
  var restoreTimer = null;
  var expectingPromotionResult = false;
  var expectingPromotionTimer = null;
  var lastNotifyUrl = "/cart/change.js";
  var debug = function () {
    if (!window.__dealeasyCroCartBridgeDebug) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift("[DealEasy↔CRO bridge]");
    console.log.apply(console, args);
  };

  // DealEasy attaches the handler on lbDealeasy (not window.vdWidgetHandler).
  function getVdHandler() {
    if (window.lbDealeasy && window.lbDealeasy.vdWidgetHandler) {
      return window.lbDealeasy.vdWidgetHandler;
    }
    if (window.vdWidgetHandler) return window.vdWidgetHandler;
    return null;
  }

  // Patch Document.querySelector(All) so DealEasy can see CRO shadow nodes.
  // Must use originalQS inside getShadowRoot — never the patched document.querySelector —
  // or HOST_SELECTOR lookups recurse forever.
  var originalQS = Document.prototype.querySelector;
  var originalQSA = Document.prototype.querySelectorAll;

  function getShadowRoot() {
    var host = originalQS.call(document, HOST_SELECTOR);
    return host && host.shadowRoot ? host.shadowRoot : null;
  }

  function onCartPage() {
    return window.location.pathname.indexOf("/cart") !== -1;
  }

  function shouldPierce(selector) {
    if (!selector || typeof selector !== "string") return false;
    // Never pierce when looking up the host itself (lives in light DOM).
    var trimmed = selector.trim();
    if (trimmed === HOST_SELECTOR || trimmed === "cro-cart-shadow-host")
      return false;

    // Do NOT pierce lb-vd-nudge. DealEasy calls removeWidgetOnPage("vd-nudge") via
    // document.querySelectorAll("lb-vd-nudge") — if we expose shadow nudges, DealEasy
    // deletes them and we reinject → appear/disappear loop.
    return (
      selector.indexOf("cro-line-item") !== -1 ||
      selector.indexOf("::part(") !== -1
    );
  }

  function pierceQuerySelector(selector) {
    var root = getShadowRoot();
    if (!root) return null;
    try {
      return root.querySelector(normalizeShadowSelector(selector));
    } catch (e) {
      return null;
    }
  }

  function pierceQuerySelectorAll(selector) {
    var root = getShadowRoot();
    if (!root) return [];
    try {
      return Array.prototype.slice.call(
        root.querySelectorAll(normalizeShadowSelector(selector)),
      );
    } catch (e) {
      return [];
    }
  }

  function mergeQuerySelectorAll(lightList, selector) {
    var merged = Array.prototype.slice.call(lightList);
    if (!shouldPierce(selector)) return lightList;
    var pierced = pierceQuerySelectorAll(selector);
    if (!pierced.length) return merged.length ? merged : lightList;
    var seen = typeof Set === "function" ? new Set(merged) : null;
    for (var i = 0; i < pierced.length; i++) {
      var el = pierced[i];
      if (seen) {
        if (!seen.has(el)) {
          seen.add(el);
          merged.push(el);
        }
      } else if (merged.indexOf(el) === -1) {
        merged.push(el);
      }
    }
    return merged;
  }

  Document.prototype.querySelector = function (selector) {
    var el = originalQS.call(this, selector);
    if (el || !shouldPierce(selector)) return el;
    return pierceQuerySelector(selector);
  };

  Document.prototype.querySelectorAll = function (selector) {
    var list = originalQSA.call(this, selector);
    if (!shouldPierce(selector)) return list;
    return mergeQuerySelectorAll(list, selector);
  };

  function ensureStylesInShadow(root) {
    if (!root) return;

    var footerRule =
      '.cro-line-item__footer,[part="line-item-footer"]{margin-bottom:8px!important}';
    var existing = root.getElementById(STYLE_ID);
    if (existing) {
      if (existing.textContent.indexOf("cro-line-item__footer") === -1) {
        existing.textContent += "\n" + footerRule;
      }
      return;
    }

    var css = NUDGE_CSS;
    var styles = document.querySelectorAll("style");
    for (var i = 0; i < styles.length; i++) {
      var text = styles[i].textContent || "";
      if (text.indexOf("lb-vd-nudge") !== -1) {
        css += "\n" + text;
      }
    }

    // Re-assert after copied DealEasy head styles, which reset padding to 0.
    css += "\nlb-vd-nudge{padding-top:8px!important}";
    css +=
      "\nlb-vd-nudge:not(:has(.lb-vd-nudge-discount-text)){visibility:hidden!important;height:0;overflow:hidden;padding:0!important;margin:0!important}";
    css += "\n" + footerRule;

    var styleEl = document.createElement("style");
    styleEl.id = STYLE_ID;
    styleEl.textContent = css;
    root.appendChild(styleEl);
  }

  function getPark(root) {
    if (!root) return null;
    var park = root.getElementById(PARK_ID);
    if (park) return park;
    park = document.createElement("div");
    park.id = PARK_ID;
    park.setAttribute("aria-hidden", "true");
    root.appendChild(park);
    return park;
  }

  function collectNudgesFromNode(node, bucket) {
    if (!node || node.nodeType !== 1) return;
    if (node.id === PARK_ID || node.id === STYLE_ID) return;
    if (node.matches && node.matches("lb-vd-nudge")) {
      bucket.push(node);
      return;
    }
    if (node.querySelectorAll) {
      var found = node.querySelectorAll("lb-vd-nudge");
      for (var i = 0; i < found.length; i++) bucket.push(found[i]);
    }
  }

  function parkNudgesFromNodes(root, nodes) {
    if (!root || !nodes || !nodes.length) return [];
    var park = getPark(root);
    var parked = [];
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      // Only salvage when CRO removes a line/container. If the removed node is
      // the nudge itself, this bridge moved it (park ↔ line) — parking again
      // causes an infinite remount loop that freezes the tab.
      if (!node || node.nodeType !== 1) continue;
      if (node.id === PARK_ID || node.id === STYLE_ID) continue;
      if (node.matches && node.matches("lb-vd-nudge")) continue;

      var list = [];
      collectNudgesFromNode(node, list);
      for (var j = 0; j < list.length; j++) {
        var el = list[j];
        if (!el || el.id === PARK_ID) continue;
        park.appendChild(el);
        parked.push(el);
      }
    }
    return parked;
  }

  function normalizeVariantId(value) {
    if (value == null) return "";
    var str = String(value);
    if (str.indexOf("/") !== -1) str = str.split("/").pop();
    return str;
  }

  function getPositionConfig(widgetSetup) {
    var position = (widgetSetup && widgetSetup.position) || {};
    if (
      screen.width <= 480 &&
      position.mobilePosition &&
      position.mobilePosition.enabled
    ) {
      position = position.mobilePosition;
    }

    return {
      lineSelector: normalizeShadowSelector(
        position.cartDrawerLineCssSelector || LINE_SELECTOR,
      ),
      targetSelector: normalizeShadowSelector(
        position.cartDrawerLineItemCssSelector || TARGET_SELECTOR,
      ),
      insertPosition: position.cartDrawerLineItemPosition || POSITION,
    };
  }

  // Prefer relative selectors from DealEasy admin (e.g. .cro-line-item).
  // Legacy host/::part forms are rewritten so older configs still work.
  function normalizeShadowSelector(selector) {
    if (!selector || typeof selector !== "string") return selector;
    var trimmed = selector.trim();
    if (!trimmed) return LINE_SELECTOR;

    var partMatch = trimmed.match(/::part\(\s*([a-zA-Z0-9_-]+)\s*\)\s*$/);
    if (partMatch) {
      var partName = partMatch[1];
      if (partName === "line-item") return LINE_SELECTOR;
      if (partName === "line-item-content") return TARGET_SELECTOR;
      return '[part="' + partName + '"]';
    }

    if (trimmed.indexOf(HOST_SELECTOR) === 0) {
      var rest = trimmed.slice(HOST_SELECTOR.length).replace(/^[\s>+~]+/, "");
      if (rest) return normalizeShadowSelector(rest);
      return LINE_SELECTOR;
    }

    return trimmed;
  }

  function findLineElement(root, lineSelector, cartLineItem) {
    var selector = normalizeShadowSelector(lineSelector) || LINE_SELECTOR;
    var lines = Array.prototype.slice.call(root.querySelectorAll(selector));
    if (!lines.length && selector !== LINE_SELECTOR) {
      lines = Array.prototype.slice.call(root.querySelectorAll(LINE_SELECTOR));
    }
    if (!lines.length) return null;

    if (cartLineItem && typeof cartLineItem.index === "number") {
      var byIndex = lines[cartLineItem.index];
      if (byIndex) return byIndex;
    }

    var variantId = normalizeVariantId(
      (cartLineItem && (cartLineItem.variantId || cartLineItem.variant_id)) ||
        "",
    );
    if (variantId) {
      for (var i = 0; i < lines.length; i++) {
        var line = lines[i];
        if (
          line.getAttribute("data-variant-id") === variantId ||
          line.getAttribute("data-variant") === variantId ||
          line.querySelector('a[href*="variant=' + variantId + '"]') ||
          line.querySelector('[data-cro-qty-minus^="' + variantId + ':"]') ||
          line.querySelector('[data-cro-qty-plus^="' + variantId + ':"]') ||
          line.querySelector('[data-cro-remove^="' + variantId + ':"]')
        ) {
          return line;
        }
      }
    }

    return null;
  }

  function clearShadowNudges(root) {
    if (!root) return;
    var existing = root.querySelectorAll("lb-vd-nudge");
    for (var i = 0; i < existing.length; i++) existing[i].remove();
  }

  // DealEasy only emits a promotion while a later volume-discount tier exists
  // (tiers.find(tier => tier.quantity > currentQty)). Empty cartLinePromotions
  // after a cart action means the customer hit the last tier — remove the nudge.
  // Empty setups with no cart action are tab-freeze noise and must be ignored.
  function markExpectingPromotionResult() {
    expectingPromotionResult = true;
    if (expectingPromotionTimer) clearTimeout(expectingPromotionTimer);
    expectingPromotionTimer = setTimeout(function () {
      expectingPromotionResult = false;
      expectingPromotionTimer = null;
    }, 4000);
  }

  function consumeExpectingPromotionResult() {
    var was = expectingPromotionResult;
    expectingPromotionResult = false;
    if (expectingPromotionTimer) {
      clearTimeout(expectingPromotionTimer);
      expectingPromotionTimer = null;
    }
    return was;
  }

  function clearPromotionsAndNudges(reason) {
    pendingPromotions = [];
    lastInjectFingerprint = "";
    clearShadowNudges(getShadowRoot());
    debug("cleared nudges — no more promotions", reason);
  }

  function applyDealEasyPromotions(promos, reason) {
    if (promos && promos.length) {
      consumeExpectingPromotionResult();
      pendingPromotions = promos;
      injectNow(reason);
      return;
    }
    if (consumeExpectingPromotionResult()) {
      clearPromotionsAndNudges(reason);
      return;
    }
    debug("ignored empty promotions", reason);
  }

  function applyNudgeProps(
    el,
    promo,
    widgetSetup,
    translation,
    shopCurrency,
    presentmentCurrency,
  ) {
    el.discount = promo.discount
      ? Object.assign({}, promo.discount)
      : promo.discount;
    el.cartLineItem = promo.cartLineItem
      ? Object.assign({}, promo.cartLineItem)
      : promo.cartLineItem;
    el.setup = widgetSetup;
    el.translation = translation;
    el.shopCurrency = shopCurrency;
    el.presentmentCurrency = presentmentCurrency;
  }

  function getPromotionsFingerprint(promotions) {
    if (!promotions || !promotions.length) return "";
    var parts = [];
    for (var i = 0; i < promotions.length; i++) {
      var promo = promotions[i] || {};
      var line = promo.cartLineItem || {};
      var discount = promo.discount || {};
      parts.push(
        [
          normalizeVariantId(line.variantId || line.variant_id),
          line.quantity,
          line.quantityToQualify,
          discount.id,
          discount.discountValue,
          discount.discountType,
        ].join(":"),
      );
    }
    return parts.join("|");
  }

  function getLineQty(line) {
    var input = line.querySelector(
      'input[type="number"], input[part="qty-input"], [part="qty-input"]',
    );
    if (input && input.value) return String(input.value).trim();
    var qtyEl = line.querySelector('.cro-qty-control, [part="qty-control"]');
    if (!qtyEl) return "";
    var text = (qtyEl.textContent || "").replace(/[^0-9]/g, "");
    return text;
  }

  function getCartDomSignature(root) {
    if (!root) return "";
    var lines = root.querySelectorAll(LINE_SELECTOR);
    var parts = [];
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var href = "";
      var link = line.querySelector('a[href*="variant="]');
      if (link) href = link.getAttribute("href") || "";
      parts.push(href + "@" + getLineQty(line));
    }
    return parts.join("|");
  }

  function variantIdFromHref(href) {
    if (!href) return "";
    var match = String(href).match(/variant=(\d+)/);
    return match ? match[1] : "";
  }

  function parseCartSignature(sig) {
    if (!sig) return [];
    return sig.split("|").map(function (part) {
      var at = part.lastIndexOf("@");
      return { href: part.slice(0, at), qty: part.slice(at + 1) };
    });
  }

  function signatureIsComplete(sig) {
    var parts = parseCartSignature(sig);
    if (!parts.length) return false;
    for (var i = 0; i < parts.length; i++) {
      if (!variantIdFromHref(parts[i].href) || !parts[i].qty) return false;
    }
    return true;
  }

  // CRO rebuilds (and tab-return redraws) often drop href or qty for a frame.
  // Only treat line-count, variant, or numeric qty changes as a real cart change.
  function isRealCartSignatureChange(previous, next) {
    if (!next || previous === next) return false;
    if (!previous) return signatureIsComplete(next);

    var prevParts = parseCartSignature(previous);
    var nextParts = parseCartSignature(next);
    if (prevParts.length !== nextParts.length) return true;
    for (var i = 0; i < prevParts.length; i++) {
      var prevVar = variantIdFromHref(prevParts[i].href);
      var nextVar = variantIdFromHref(nextParts[i].href);
      if (prevVar && nextVar && prevVar !== nextVar) return true;
      if (
        prevParts[i].qty &&
        nextParts[i].qty &&
        prevParts[i].qty !== nextParts[i].qty
      ) {
        return true;
      }
    }
    return false;
  }

  function promotionsMatchDomQty(root, promotions) {
    if (!root || !promotions || !promotions.length) return true;
    for (var i = 0; i < promotions.length; i++) {
      var line = promotions[i].cartLineItem || {};
      var el = findLineElement(root, LINE_SELECTOR, line);
      if (!el) continue;
      var domQty = getLineQty(el);
      if (domQty && String(line.quantity) !== String(domQty)) return false;
    }
    return true;
  }

  function getMountedNudges(root) {
    if (!root) return [];
    var all = root.querySelectorAll("lb-vd-nudge");
    var mounted = [];
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.closest && el.closest("#" + PARK_ID)) continue;
      mounted.push(el);
    }
    return mounted;
  }

  // Chrome/Safari often drop Stencil's inner light DOM after the tab or window
  // loses focus. The custom element stays put, so a count-only health check
  // thinks mounts are fine while :has(.lb-vd-nudge-discount-text) hides them.
  function nudgeIsPainted(el) {
    return !!(
      el &&
      el.querySelector &&
      el.querySelector(".lb-vd-nudge-discount-text, .lb-vd-nudge-atc-btn")
    );
  }

  function shadowNudgesHealthy(root, expectedCount) {
    if (!root) return false;
    var nudges = getMountedNudges(root);
    if (nudges.length !== expectedCount) return false;
    for (var i = 0; i < nudges.length; i++) {
      if (!nudges[i].closest || !nudges[i].closest(LINE_SELECTOR)) return false;
      if (!nudgeIsPainted(nudges[i])) return false;
    }
    return true;
  }

  function currentMountsAreValid(root) {
    if (!root) return false;
    var promotions =
      pendingPromotions ||
      (function () {
        var handler = getVdHandler();
        var setup =
          handler && typeof handler.getNudgeSetup === "function"
            ? handler.getNudgeSetup()
            : null;
        return (setup && setup.cartLinePromotions) || null;
      })();
    if (!promotions || !promotions.length) {
      return false;
    }
    var fingerprint = getPromotionsFingerprint(promotions);
    return (
      !!fingerprint &&
      fingerprint === lastInjectFingerprint &&
      shadowNudgesHealthy(root, promotions.length) &&
      promotionsMatchDomQty(root, promotions)
    );
  }

  function takeExistingNudge(root, line, variantId) {
    var el = line.querySelector("lb-vd-nudge");
    if (el) return el;
    var park = root.getElementById(PARK_ID);
    if (!park) return null;
    var parked = park.querySelectorAll("lb-vd-nudge");
    if (variantId) {
      for (var i = 0; i < parked.length; i++) {
        var item = parked[i].cartLineItem || {};
        if (
          normalizeVariantId(item.variantId || item.variant_id) === variantId
        ) {
          return parked[i];
        }
      }
    }
    return parked[0] || null;
  }

  function injectIntoShadow(position, promotions) {
    var root = getShadowRoot();
    if (!root) return false;

    if (bridgeInjecting) {
      debug("skip nested inject");
      return true;
    }

    var expectedCount = promotions && promotions.length ? promotions.length : 0;
    var fingerprint = getPromotionsFingerprint(promotions);
    if (
      fingerprint &&
      fingerprint === lastInjectFingerprint &&
      shadowNudgesHealthy(root, expectedCount) &&
      promotionsMatchDomQty(root, promotions)
    ) {
      debug("skip inject — unchanged", fingerprint);
      return true;
    }

    var handler = getVdHandler();
    var nudgeSetup =
      handler && typeof handler.getNudgeSetup === "function"
        ? handler.getNudgeSetup()
        : null;
    var widgetSetup = (nudgeSetup && nudgeSetup.widgetSetup) || {};
    var shop = (nudgeSetup && nudgeSetup.shop) || {};
    var cfg = getPositionConfig(widgetSetup);
    if (position) {
      if (
        screen.width <= 480 &&
        position.mobilePosition &&
        position.mobilePosition.enabled
      ) {
        position = position.mobilePosition;
      }
      cfg.lineSelector = normalizeShadowSelector(
        position.cartDrawerLineCssSelector || cfg.lineSelector,
      );
      cfg.targetSelector = normalizeShadowSelector(
        position.cartDrawerLineItemCssSelector || cfg.targetSelector,
      );
      cfg.insertPosition =
        position.cartDrawerLineItemPosition || cfg.insertPosition;
    }

    bridgeInjecting = true;
    var injected = 0;
    try {
      ensureStylesInShadow(root);

      if (!promotions || !promotions.length) {
        clearShadowNudges(root);
        lastInjectFingerprint = "";
        debug("no promotions to inject", { hasSetup: !!nudgeSetup, cfg: cfg });
        return false;
      }

      var translation =
        (widgetSetup.translations &&
          (widgetSetup.translations[(window.Shopify && Shopify.locale) || ""] ||
            widgetSetup.translations.default)) ||
        {};
      var presentmentCurrency = {
        code:
          window.Shopify && Shopify.currency
            ? Shopify.currency.active
            : undefined,
        rate:
          window.Shopify && Shopify.currency
            ? Shopify.currency.rate
            : undefined,
      };
      var kept = typeof Set === "function" ? new Set() : [];

      function track(el) {
        if (kept && kept.add) kept.add(el);
        else kept.push(el);
      }
      function wasKept(el) {
        if (kept && kept.has) return kept.has(el);
        return kept.indexOf(el) !== -1;
      }

      for (var i = 0; i < promotions.length; i++) {
        var promo = promotions[i];
        var line = findLineElement(root, cfg.lineSelector, promo.cartLineItem);
        if (!line) {
          debug(
            "no line for promo",
            promo && promo.cartLineItem,
            cfg.lineSelector,
          );
          continue;
        }

        var target =
          line.querySelector(cfg.targetSelector) ||
          line.querySelector(TARGET_SELECTOR) ||
          line;

        // Reuse the parked/existing custom element so Stencil does not remount.
        var el = takeExistingNudge(
          root,
          line,
          normalizeVariantId(
            promo.cartLineItem &&
              (promo.cartLineItem.variantId || promo.cartLineItem.variant_id),
          ),
        );
        var created = false;
        // After tab/window blur, Stencil often leaves an empty host. Reusing it
        // does not repaint; replace it so the custom element boots again.
        if (el && el.cartLineItem && !nudgeIsPainted(el)) {
          try {
            el.remove();
          } catch (e) {}
          el = null;
        }
        if (!el) {
          el = document.createElement("lb-vd-nudge");
          created = true;
        }
        if (el.parentNode !== target) {
          try {
            target.insertAdjacentElement(cfg.insertPosition, el);
          } catch (insertErr) {
            debug("insert failed, falling back to beforeend", insertErr);
            target.insertAdjacentElement("beforeend", el);
          }
        }

        track(el);

        var assignFn = (function (element, promotion) {
          return function () {
            applyNudgeProps(
              element,
              promotion,
              widgetSetup,
              translation,
              shop.shopCurrency,
              presentmentCurrency,
            );
          };
        })(el, promo);

        if (
          window.customElements &&
          customElements.get &&
          customElements.get("lb-vd-nudge")
        ) {
          assignFn();
        } else if (window.customElements && customElements.whenDefined) {
          customElements.whenDefined("lb-vd-nudge").then(assignFn);
        } else {
          assignFn();
        }
        if (created) debug("created nudge for line");
        injected++;
      }

      // Drop orphans from a previous cart state (not wholesale clear-first).
      var existing = root.querySelectorAll("lb-vd-nudge");
      for (var r = 0; r < existing.length; r++) {
        if (!wasKept(existing[r])) existing[r].remove();
      }

      lastInjectFingerprint = injected > 0 ? fingerprint : "";
      lastDomSignature = getCartDomSignature(root);

      if (handler) {
        handler.vdNudgeRenderCount = injected;
        if (
          !nudgeListenersRegistered &&
          typeof handler.registerVdNudgeListeners === "function"
        ) {
          handler.registerVdNudgeListeners();
          nudgeListenersRegistered = true;
        }
      }

      debug("injected", injected, "nudges with", cfg);
      return injected > 0;
    } finally {
      setTimeout(function () {
        bridgeInjecting = false;
        if (pendingRestoreAfterInject) {
          pendingRestoreAfterInject = false;
          injectNow("after-inject-mutations");
        }
      }, 0);
    }
  }

  function resolvePromotions() {
    if (pendingPromotions && pendingPromotions.length) return pendingPromotions;
    var handler = getVdHandler();
    var setup =
      handler && typeof handler.getNudgeSetup === "function"
        ? handler.getNudgeSetup()
        : null;
    return (setup && setup.cartLinePromotions) || null;
  }

  function resolvePosition() {
    if (pendingPosition) return pendingPosition;
    var handler = getVdHandler();
    var setup =
      handler && typeof handler.getNudgeSetup === "function"
        ? handler.getNudgeSetup()
        : null;
    return (setup && setup.widgetSetup && setup.widgetSetup.position) || null;
  }

  function injectNow(reason) {
    if (onCartPage()) return;
    var root = getShadowRoot();
    if (!root || !root.querySelector(LINE_SELECTOR)) {
      debug("injectNow skipped — no line items", reason);
      return;
    }
    ensureStylesInShadow(root);
    patchVdWidgetHandler();

    var promotions = resolvePromotions();
    if (promotions && promotions.length) {
      try {
        injectIntoShadow(resolvePosition(), promotions);
        debug("injectNow", reason);
      } catch (e) {
        console.warn("[DealEasy↔CRO bridge] injectNow failed", e);
      }
      return;
    }

    // Do NOT call renderVdNudge with empty setup — DealEasy disconnects listeners.
    // On focus-return, notifying again would race an empty DealEasy setup.
    var skipNotify =
      reason === "visibility-return" ||
      reason === "window-focus" ||
      reason === "pageshow" ||
      reason === "focus-return";
    if (skipNotify) {
      debug("injectNow waiting for promotions — skip notify", reason);
      return;
    }
    debug("injectNow waiting for promotions", reason);
    notifyDealEasyCartChanged(lastNotifyUrl, reason);
  }

  function reparentParkedNudges(root) {
    if (!root) return false;
    var promotions = resolvePromotions();
    if (!promotions || !promotions.length) return false;
    if (shadowNudgesHealthy(root, promotions.length)) return true;
    if (!root.querySelector(LINE_SELECTOR)) return false;
    try {
      return injectIntoShadow(resolvePosition(), promotions);
    } catch (e) {
      debug("reparent failed", e);
      return false;
    }
  }

  function isCartActionUrl(url) {
    if (!url || typeof url !== "string") return false;
    var absolute =
      url.indexOf("https://") === 0 || url.indexOf("http://") === 0
        ? url
        : "https://" +
          window.location.hostname +
          (url.charAt(0) === "/" ? url : "/" + url);
    var parsed;
    try {
      parsed = new URL(absolute);
    } catch (e) {
      return CART_ACTION_PATHS.some(function (path) {
        return url.indexOf(path) !== -1;
      });
    }
    var src = parsed.searchParams.get("src");
    if (src === "dealeasy" || src === "selleasy") return false;
    if (
      typeof window.dealeasySourcesToIgnore === "function" &&
      window.dealeasySourcesToIgnore(parsed)
    ) {
      return false;
    }
    return CART_ACTION_PATHS.some(function (path) {
      return parsed.pathname.indexOf(path) !== -1 || url.indexOf(path) !== -1;
    });
  }

  function getFetchUrl(input) {
    if (!input) return "";
    if (typeof input === "string") return input;
    if (typeof input.url === "string") return input.url;
    return "";
  }

  function notifyDealEasyCartChanged(url, reason) {
    lastNotifyUrl = url || lastNotifyUrl || "/cart/change.js";
    // Only treat real cart mutations as "DealEasy may now return no more upsells".
    // Drawer open / focus / already-open must not clear a still-valid nudge.
    if (
      reason === "fetch" ||
      reason === "xhr" ||
      reason === "cart-dom-signature" ||
      reason === "lb-dealeasy-vdn-did-atc" ||
      reason === "lb-dealeasy-cart-updated" ||
      reason === "manual"
    ) {
      markExpectingPromotionResult();
    }
    if (notifyTimer) clearTimeout(notifyTimer);
    notifyTimer = setTimeout(function () {
      notifyTimer = null;
      var requestURL = lastNotifyUrl;
      if (!isCartActionUrl(requestURL)) requestURL = "/cart/change.js";
      debug("notify DealEasy cart action", reason, requestURL);
      window.dispatchEvent(
        new CustomEvent("lb-dealeasy-network-request", {
          detail: { requestURL: requestURL },
        }),
      );
    }, NOTIFY_MS);
  }

  function watchNetworkForCartActions() {
    if (window.__dealeasyCroCartBridgeNetworkWatch) return;
    window.__dealeasyCroCartBridgeNetworkWatch = true;

    if (typeof window.fetch === "function") {
      var innerFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = getFetchUrl(input);
        var result = innerFetch.apply(this, arguments);
        if (
          isCartActionUrl(url) &&
          result &&
          typeof result.then === "function"
        ) {
          markExpectingPromotionResult();
          result
            .then(function () {
              notifyDealEasyCartChanged(url, "fetch");
            })
            .catch(function () {});
        }
        return result;
      };
      try {
        window.fetch.__croBridgeFetch = true;
      } catch (e) {}
    }

    if (window.XMLHttpRequest && XMLHttpRequest.prototype) {
      var origOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        var href = typeof url === "string" ? url : getFetchUrl(url);
        this.addEventListener("load", function () {
          if (isCartActionUrl(href)) {
            markExpectingPromotionResult();
            notifyDealEasyCartChanged(href, "xhr");
          }
        });
        return origOpen.apply(this, arguments);
      };
    }
  }

  function cacheSetup(setup) {
    if (!setup) return;
    pendingPosition =
      (setup.widgetSetup && setup.widgetSetup.position) || pendingPosition;
    var promos = setup.cartLinePromotions;
    // DealEasy often emits an empty setup when the tab is frozen/refocused.
    // Keep the last known promotions so we can remount without waiting on a
    // new cart-action network request.
    if (promos && promos.length) pendingPromotions = promos;
  }

  function patchVdWidgetHandler() {
    var handler = getVdHandler();
    if (!handler || patchedHandler) return !!handler && patchedHandler;
    patchedHandler = true;

    if (typeof handler.setNudgeSetup === "function") {
      var originalSet = handler.setNudgeSetup.bind(handler);
      handler.setNudgeSetup = function (setup) {
        var result = originalSet(setup);
        cacheSetup(setup);
        var promos = (setup && setup.cartLinePromotions) || [];
        var emptyRoot = getShadowRoot();
        if (!emptyRoot || !emptyRoot.querySelector(LINE_SELECTOR)) {
          consumeExpectingPromotionResult();
          clearPromotionsAndNudges("setNudgeSetup-empty-cart");
        } else {
          applyDealEasyPromotions(promos, "setNudgeSetup");
        }
        return result;
      };
    }

    if (typeof handler.positionVdNudge === "function") {
      var originalPosition = handler.positionVdNudge.bind(handler);
      handler.positionVdNudge = function (position, promotions) {
        if (onCartPage()) {
          return originalPosition(position, promotions);
        }
        pendingPosition = position || pendingPosition;
        if (promotions && promotions.length) pendingPromotions = promotions;
        var root = getShadowRoot();
        if (!root || !root.querySelector(LINE_SELECTOR)) {
          debug("positionVdNudge cached — waiting for line items");
          return true;
        }
        if (Object.prototype.toString.call(promotions) === "[object Array]") {
          applyDealEasyPromotions(promotions, "positionVdNudge");
        } else {
          injectNow("positionVdNudge");
        }
        return true;
      };
    }

    if (typeof handler.observeElementRemoval === "function") {
      handler.observeElementRemoval = function () {
        debug("suppressed observeElementRemoval");
        return true;
      };
    }

    if (typeof handler.disconnectListeners === "function") {
      var originalDisconnect = handler.disconnectListeners.bind(handler);
      handler.disconnectListeners = function () {
        var setup =
          typeof handler.getNudgeSetup === "function"
            ? handler.getNudgeSetup()
            : null;
        var promos = setup && setup.cartLinePromotions;
        var disconnectRoot = getShadowRoot();
        if (!promos || !promos.length) {
          if (
            !disconnectRoot ||
            !disconnectRoot.querySelector(LINE_SELECTOR) ||
            expectingPromotionResult
          ) {
            consumeExpectingPromotionResult();
            clearPromotionsAndNudges("disconnectListeners");
          }
        }
        return originalDisconnect();
      };
    }

    debug("patched vdWidgetHandler");
    return true;
  }

  function observeShadowRoot(root) {
    if (shadowObserved || !root || typeof MutationObserver === "undefined")
      return;
    shadowObserved = true;
    ensureStylesInShadow(root);

    var overlay = root.querySelector(".cro-cart-overlay");
    var wasOpen = !!(overlay && overlay.classList.contains("is-open"));
    lastDomSignature = getCartDomSignature(root);

    var observer = new MutationObserver(function (mutations) {
      if (bridgeInjecting) {
        pendingRestoreAfterInject = true;
        return;
      }

      overlay = overlay || root.querySelector(".cro-cart-overlay");
      var isOpen = !!(overlay && overlay.classList.contains("is-open"));

      var removed = [];
      var lineDomChanged = false;
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (mutation.type !== "childList") continue;

        for (var r = 0; r < mutation.removedNodes.length; r++) {
          removed.push(mutation.removedNodes[r]);
        }

        var nodes = [];
        var j;
        for (j = 0; j < mutation.addedNodes.length; j++)
          nodes.push(mutation.addedNodes[j]);
        for (j = 0; j < mutation.removedNodes.length; j++)
          nodes.push(mutation.removedNodes[j]);

        for (j = 0; j < nodes.length; j++) {
          var node = nodes[j];
          if (!(node instanceof Element)) continue;
          if (node.id === STYLE_ID || node.id === PARK_ID) continue;
          if (
            node.matches &&
            (node.matches("lb-vd-nudge") ||
              node.querySelector("lb-vd-nudge") ||
              (typeof node.closest === "function" &&
                node.closest("lb-vd-nudge")))
          ) {
            continue;
          }
          if (
            node.matches &&
            (node.matches(LINE_SELECTOR) ||
              node.querySelector(LINE_SELECTOR) ||
              node.matches(".cro-cart-items") ||
              node.querySelector(".cro-cart-items"))
          ) {
            lineDomChanged = true;
            break;
          }
        }
      }

      if (removed.length) parkNudgesFromNodes(root, removed);

      var signature = getCartDomSignature(root);
      var signatureChanged = isRealCartSignatureChange(
        lastDomSignature,
        signature,
      );

      if (isOpen && lineDomChanged) {
        // Same cart contents, new DOM — keep the live custom element.
        reparentParkedNudges(root);
      }

      if (isOpen && !wasOpen) {
        if (!currentMountsAreValid(root)) {
          injectNow("drawer-open");
          if (!resolvePromotions() || !resolvePromotions().length) {
            notifyDealEasyCartChanged(lastNotifyUrl, "drawer-open");
          }
        } else {
          debug("drawer reopen — keeping existing nudges");
        }
      } else if (isOpen && signatureChanged) {
        notifyDealEasyCartChanged("/cart/change.js", "cart-dom-signature");
      } else if (isOpen && lineDomChanged && !currentMountsAreValid(root)) {
        injectNow("line-items-changed");
      }

      wasOpen = isOpen;
      if (signatureIsComplete(signature)) lastDomSignature = signature;
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "value"],
    });

    if (wasOpen) {
      lastDomSignature = getCartDomSignature(root);
      if (!currentMountsAreValid(root)) {
        injectNow("already-open");
        notifyDealEasyCartChanged(lastNotifyUrl, "already-open");
      }
    }
  }

  function waitForShadowHost() {
    var root = getShadowRoot();
    if (root) {
      observeShadowRoot(root);
      return;
    }

    if (typeof MutationObserver === "undefined") {
      setTimeout(waitForShadowHost, 250);
      return;
    }

    var hostObserver = new MutationObserver(function () {
      var found = getShadowRoot();
      if (!found) return;
      hostObserver.disconnect();
      observeShadowRoot(found);
    });

    hostObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  function waitForDealEasy(attempt) {
    attempt = attempt || 0;
    var handler = getVdHandler();
    if (handler) {
      patchVdWidgetHandler();
      cacheSetup(
        typeof handler.getNudgeSetup === "function"
          ? handler.getNudgeSetup()
          : null,
      );
      var root = getShadowRoot();
      if (
        root &&
        root.querySelector(LINE_SELECTOR) &&
        !currentMountsAreValid(root)
      ) {
        injectNow("dealeasy-ready");
      }
      return;
    }
    if (attempt > 120) {
      debug("timed out waiting for lbDealeasy.vdWidgetHandler");
      return;
    }
    setTimeout(function () {
      waitForDealEasy(attempt + 1);
    }, 250);
  }

  window.addEventListener("lb-dealeasy-vdn-did-atc", function () {
    lastInjectFingerprint = "";
    notifyDealEasyCartChanged("/cart/add.js", "lb-dealeasy-vdn-did-atc");
  });
  window.addEventListener("lb-dealeasy-cart-updated", function () {
    lastInjectFingerprint = "";
    notifyDealEasyCartChanged("/cart/change.js", "lb-dealeasy-cart-updated");
  });
  window.addEventListener("lb-dealeasy-widget-did-load", function () {
    injectNow("lb-dealeasy-widget-did-load");
  });
  function restoreNudgesAfterFocus(reason) {
    if (document.hidden) return;
    var root = getShadowRoot();
    if (!root) return;
    var overlay = root.querySelector(".cro-cart-overlay");
    if (!overlay || !overlay.classList.contains("is-open")) return;
    if (currentMountsAreValid(root)) return;
    // Force a remount/re-paint. Fingerprint skip would leave an empty
    // custom element hidden by :has(.lb-vd-nudge-discount-text).
    lastInjectFingerprint = "";
    injectNow(reason || "focus-return");
  }

  function scheduleRestoreAfterFocus(reason) {
    if (document.hidden) return;
    if (restoreTimer) clearTimeout(restoreTimer);
    restoreTimer = setTimeout(function () {
      restoreTimer = null;
      restoreNudgesAfterFocus(reason);
    }, 80);
  }

  document.addEventListener("visibilitychange", function () {
    if (document.hidden) return;
    scheduleRestoreAfterFocus("visibility-return");
  });
  window.addEventListener("focus", function () {
    scheduleRestoreAfterFocus("window-focus");
  });
  window.addEventListener("pageshow", function () {
    scheduleRestoreAfterFocus("pageshow");
  });

  // Public helper for manual verification in DevTools:
  // window.__dealeasyCroCartBridgeDebug = true; window.__dealeasyCroCartBridgeInject()
  window.__dealeasyCroCartBridgeInject = function () {
    lastInjectFingerprint = "";
    injectNow("manual");
    notifyDealEasyCartChanged("/cart/change.js", "manual");
  };

  // DealEasy registers $(document).ajaxComplete and unconditionally calls
  // jqXHR.getResponseHeader(). Klaviyo onsite.js (and similar) can trigger
  // ajaxComplete without a jqXHR, which DealEasy then logs as:
  // "jQuery ajax listener error: TypeError: Cannot read properties of undefined..."
  // Wrap ajaxComplete registrations so handlers skip incomplete events.
  // Start immediately (this file loads before app embeds) so the patch is in
  // place before DealEasy binds its listener.
  function guardJqueryAjaxComplete(attempt) {
    attempt = attempt || 0;
    var $ = window.jQuery || window.$;
    if (!$ || !$.fn || typeof $.fn.ajaxComplete !== "function") {
      if (attempt < 80)
        setTimeout(function () {
          guardJqueryAjaxComplete(attempt + 1);
        }, 100);
      return;
    }
    if ($.fn.__dealeasyCroAjaxGuarded) return;
    $.fn.__dealeasyCroAjaxGuarded = true;

    var originalAjaxComplete = $.fn.ajaxComplete;
    $.fn.ajaxComplete = function (fn) {
      if (typeof fn !== "function")
        return originalAjaxComplete.apply(this, arguments);
      return originalAjaxComplete.call(this, function (event, xhr, settings) {
        if (!xhr || typeof xhr.getResponseHeader !== "function") return;
        return fn.apply(this, arguments);
      });
    };
    debug("guarded jQuery ajaxComplete");
  }

  watchNetworkForCartActions();
  guardJqueryAjaxComplete();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      waitForShadowHost();
      waitForDealEasy();
    });
  } else {
    waitForShadowHost();
    waitForDealEasy();
  }
})();
